/**
 * DailyExpenseEntry — Britto's daily expense entry against the 37
 * locked categories (38 after Appendix A reconciliation).
 *
 * Reads/writes:
 *   GET  /api/tdb-mis/categories            (37 locked rows)
 *   GET  /api/tdb-mis/expenses?date_from=&date_to=&city=&category=
 *   POST /api/tdb-mis/expenses              (date, city, category, amount, notes)
 *   DEL  /api/tdb-mis/expenses/{id}         (soft-delete · audit log)
 *
 * UX:
 *   • Single-line form: date · city dropdown · category dropdown · amount · notes
 *   • Category dropdown groups by bucket with icons
 *   • Shared-cost categories accept city="Group"; others city-required
 *   • Recent entries list below with delete affordance
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Calendar, MapPin, Tag, IndianRupee, FileText, Trash2, Plus,
  AlertCircle, CheckCircle2,
} from 'lucide-react';
import api from '@/lib/api';

const CITIES = ['Bengaluru', 'Mumbai', 'Gurgaon', 'Group'];
const BUCKET_ICONS = {
  food_cost: '🍞',
  manpower: '👥',
  utilities: '⚡',
  operating: '📦',
  marketing: '📣',
  admin_gateway: '💼',
  inter_branch: '🔄',
  capex: '💎',
};

function todayIST() {
  const ist = new Date(Date.now() + 5.5 * 3600 * 1000);
  return ist.toISOString().slice(0, 10);
}

function fmtRupees(n) {
  if (!n) return '₹0';
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function DailyExpenseEntry({ className = '' }) {
  const [categories, setCategories] = useState([]);
  const [entries, setEntries] = useState([]);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [date, setDate] = useState(todayIST());
  const [city, setCity] = useState('Bengaluru');
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  // Load categories + recent entries
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, e] = await Promise.all([
          api.get('/tdb-mis/categories'),
          api.get('/tdb-mis/expenses', {
            params: { date_from: todayIST().slice(0, 8) + '01' },
          }),
        ]);
        if (cancelled) return;
        setCategories(c.data.categories || []);
        setEntries(e.data.entries || []);
      } catch (ex) {
        if (!cancelled) setErr(ex?.response?.data?.detail || 'Failed to load');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Selected category metadata
  const selectedCat = useMemo(
    () => categories.find((c) => c.category === category),
    [categories, category]
  );

  // Auto-flip city to "Group" if user picks a shared-cost category
  useEffect(() => {
    if (selectedCat && selectedCat.is_shared_cost && city !== 'Group') {
      setCity('Group');
    }
    if (selectedCat && !selectedCat.is_shared_cost && city === 'Group') {
      setCity('Bengaluru');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCat]);

  // Group categories by bucket for the dropdown · filter out auto-generated
  // (e.g. Inter-branch Transfer In is auto-created when an Out is booked)
  const grouped = useMemo(() => {
    const byBucket = {};
    for (const c of categories) {
      if (c.is_auto_generated) continue;
      const b = c.bucket || 'other';
      byBucket[b] = byBucket[b] || [];
      byBucket[b].push(c);
    }
    return byBucket;
  }, [categories]);

  // Is the picked category Inter-branch Transfer Out? If yes, require a destination_city
  const isInterBranchOut = category === 'Inter-branch Transfer Out';
  const [destinationCity, setDestinationCity] = useState('Mumbai');

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    setErr('');
    setSuccess('');
    if (!category) { setErr('Pick a category'); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setErr('Amount must be > 0'); return; }
    if (isInterBranchOut && (!destinationCity || destinationCity === city)) {
      setErr('Inter-branch Transfer Out requires a destination city different from the source');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { date, city, category, amount: amt, notes };
      if (isInterBranchOut) payload.destination_city = destinationCity;
      const r = await api.post('/tdb-mis/expenses', payload);
      // Prepend to recent list — include paired_entry if backend returned one
      const newRows = [r.data.entry];
      if (r.data.paired_entry) newRows.push(r.data.paired_entry);
      setEntries((es) => [...newRows, ...es].slice(0, 100));
      const pairedNote = r.data.paired_entry
        ? ` · auto-created In at ${r.data.paired_entry.city}`
        : '';
      setSuccess(`Saved ${selectedCat?.bucket ? BUCKET_ICONS[selectedCat.bucket] : ''} ${category} · ${fmtRupees(amt)}${pairedNote}`);
      setAmount('');
      setNotes('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }, [date, city, category, amount, notes, selectedCat, isInterBranchOut, destinationCity]);

  const remove = useCallback(async (entryId) => {
    if (!window.confirm('Delete this entry? It will be soft-deleted with audit trail.')) return;
    try {
      await api.delete(`/tdb-mis/expenses/${entryId}`);
      setEntries((es) => es.filter((e) => e.entry_id !== entryId));
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Delete failed');
    }
  }, []);

  const todayTotal = useMemo(
    () => entries.filter((e) => e.date === date).reduce((s, e) => s + (e.amount || 0), 0),
    [entries, date]
  );

  return (
    <div
      data-testid="daily-expense-entry"
      className={`bg-white rounded-2xl border border-gray-200 p-6 ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">Daily Expense Entry</h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            {categories.length} categories · locked
          </span>
        </div>
        <div className="text-xs text-gray-600">
          Today: <span className="font-semibold text-gray-900">{fmtRupees(todayTotal)}</span>
        </div>
      </div>

      <form
        data-testid="daily-expense-form"
        onSubmit={submit}
        className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-4"
      >
        <div className="md:col-span-2">
          <label className="text-[11px] text-gray-500 flex items-center gap-1 mb-0.5">
            <Calendar className="w-3 h-3" /> Date
          </label>
          <input
            data-testid="daily-expense-date"
            type="date"
            value={date}
            max={todayIST()}
            onChange={(e) => setDate(e.target.value)}
            className="w-full text-sm px-2 py-1.5 rounded-md border border-gray-300 focus:border-emerald-500 outline-none"
          />
        </div>

        <div className="md:col-span-4">
          <label className="text-[11px] text-gray-500 flex items-center gap-1 mb-0.5">
            <Tag className="w-3 h-3" /> Category{selectedCat?.is_shared_cost ? ' · shared cost' : ''}
          </label>
          <select
            data-testid="daily-expense-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full text-sm px-2 py-1.5 rounded-md border border-gray-300 focus:border-emerald-500 outline-none bg-white"
            required
          >
            <option value="">-- pick category --</option>
            {Object.entries(grouped).map(([bucket, items]) => (
              <optgroup key={bucket} label={`${BUCKET_ICONS[bucket] || ''} ${bucket}`}>
                {items.map((c) => (
                  <option key={c.category} value={c.category}>
                    {c.category}{c.is_shared_cost ? ' (shared)' : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="text-[11px] text-gray-500 flex items-center gap-1 mb-0.5">
            <MapPin className="w-3 h-3" /> City
          </label>
          <select
            data-testid="daily-expense-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="w-full text-sm px-2 py-1.5 rounded-md border border-gray-300 focus:border-emerald-500 outline-none bg-white"
          >
            {CITIES.map((c) => (
              <option
                key={c}
                value={c}
                disabled={c === 'Group' && !selectedCat?.is_shared_cost}
              >{c}</option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="text-[11px] text-gray-500 flex items-center gap-1 mb-0.5">
            <IndianRupee className="w-3 h-3" /> Amount
          </label>
          <input
            data-testid="daily-expense-amount"
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full text-sm px-2 py-1.5 rounded-md border border-gray-300 focus:border-emerald-500 outline-none"
            placeholder="0"
            required
          />
        </div>

        <div className="md:col-span-2 flex items-end">
          <button
            data-testid="daily-expense-submit"
            type="submit"
            disabled={submitting}
            className="w-full text-sm px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            {submitting ? 'Saving…' : 'Add Expense'}
          </button>
        </div>

        <div className="md:col-span-12">
          <input
            data-testid="daily-expense-notes"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional · e.g. invoice number, vendor name)"
            maxLength={500}
            className="w-full text-xs px-2 py-1.5 rounded-md border border-gray-200 focus:border-emerald-500 outline-none bg-gray-50"
          />
        </div>

        {/* Inter-branch Transfer Out · destination city picker.
            Surfaces only when IB-Out is selected so Britto can't get
            it wrong. The system auto-creates the matching In at the
            destination city in the same transaction (Policy 7b). */}
        {isInterBranchOut && (
          <div
            data-testid="daily-expense-ib-destination-strip"
            className="md:col-span-12 p-2 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-900"
          >
            <div className="flex items-center gap-2">
              <span className="font-medium">🔄 Inter-branch Out · destination:</span>
              <select
                data-testid="daily-expense-ib-destination"
                value={destinationCity}
                onChange={(e) => setDestinationCity(e.target.value)}
                className="text-xs px-2 py-1 rounded-md border border-blue-300 bg-white"
              >
                {CITIES.filter((c) => c !== 'Group' && c !== city).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <span className="text-blue-700 ml-auto">
                System auto-creates matching In at {destinationCity}.
              </span>
            </div>
          </div>
        )}
      </form>

      {err && (
        <div data-testid="daily-expense-error" className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 flex items-start gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {err}
        </div>
      )}
      {success && (
        <div data-testid="daily-expense-success" className="mb-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-2 flex items-start gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {success}
        </div>
      )}

      {/* Recent entries list */}
      <div className="mt-2" data-testid="daily-expense-list">
        <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1.5">
          Recent (this month · {entries.length})
        </div>
        {entries.length === 0 ? (
          <div className="text-xs text-gray-500 italic py-2">
            No entries yet this month.
          </div>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {entries.slice(0, 30).map((e) => (
              <div
                key={e.entry_id}
                data-testid={`daily-expense-row-${e.entry_id}`}
                className="flex items-center justify-between text-xs px-2 py-1 rounded-md hover:bg-gray-50 group"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-gray-500 w-20 shrink-0">{e.date}</span>
                  <span className="text-gray-700 w-16 shrink-0">{e.city}</span>
                  <span className="font-medium text-gray-900 truncate">
                    {BUCKET_ICONS[e.bucket] || ''} {e.category}
                  </span>
                  {e.notes && <span className="text-gray-400 italic truncate">· {e.notes}</span>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-semibold text-gray-900">{fmtRupees(e.amount)}</span>
                  <span className="text-gray-400 text-[10px]">{fmtTime(e.entered_at)}</span>
                  <button
                    data-testid={`daily-expense-delete-${e.entry_id}`}
                    onClick={() => remove(e.entry_id)}
                    className="text-gray-300 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
                    title="Delete (soft-delete with audit)"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
